import { useEffect, useState } from "react";
import { Plus, X, Trash2, Youtube, Instagram, Link as LinkIcon, Folder, Video } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { ConfirmDeleteDialog } from "../../components/ConfirmDeleteDialog";

const inputCls = "w-full bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[10px] uppercase tracking-widest text-white/40 mb-1.5";

const KIND_ICON = { youtube: Youtube, instagram: Instagram, link: LinkIcon };

const VideoCard = ({ v, onDelete, adminView = true }) => {
  const Icon = KIND_ICON[v.kind] || LinkIcon;
  return (
    <div className="border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid={`workout-${v.id}`}>
      <div className="aspect-video bg-black">
        {v.embed_url ? (
          <iframe title={v.title} src={v.embed_url} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen loading="lazy" className="w-full h-full" />
        ) : (
          <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center justify-center h-full text-[#D4AF37]"><LinkIcon size={24} /></a>
        )}
      </div>
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{v.title}</p>
          <p className="text-white/40 text-xs mt-0.5 flex items-center gap-1"><Icon size={11} /> {v.category}{v.subcategory ? ` · ${v.subcategory}` : ""}</p>
          {v.notes && <p className="text-white/50 text-xs mt-1 line-clamp-2">{v.notes}</p>}
        </div>
        {adminView && onDelete && (
          <button onClick={() => onDelete(v)} className="text-red-400/70 hover:text-red-400 p-1" data-testid={`workout-delete-${v.id}`}><Trash2 size={14} /></button>
        )}
      </div>
    </div>
  );
};

const AddModal = ({ meta, players, onClose, onSaved }) => {
  const [form, setForm] = useState({ title: "", url: "", category: "Plyometrics", subcategory: "", dedicated: false, player_ids: [], notes: "" });
  const [saving, setSaving] = useState(false);
  const subs = (meta?.categories?.[form.category] || []);
  const needSub = form.category === "Football";
  const [pSearch, setPSearch] = useState("");
  const filteredPlayers = players.filter((p) => `${p.player_id} ${p.name}`.toLowerCase().includes(pSearch.toLowerCase())).slice(0, 20);

  const togglePlayer = (pid) => {
    const next = form.player_ids.includes(pid) ? form.player_ids.filter((x) => x !== pid) : [...form.player_ids, pid];
    setForm({ ...form, player_ids: next });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        title: form.title, url: form.url, category: form.category,
        subcategory: needSub ? form.subcategory : null,
        player_ids: form.dedicated ? form.player_ids : [],
        notes: form.notes,
      };
      await api.post("/admin/workouts", body);
      toast.success("Video added");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto py-10 px-4" onClick={onClose} data-testid="workout-add-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/10 p-6 sm:p-8">
        <div className="flex justify-between items-start mb-5">
          <h2 className="font-heading text-xl font-black">Add Video</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div><label className={labelCls}>Title *</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="workout-title" /></div>
          <div><label className={labelCls}>Video URL (YouTube / Instagram / any) *</label><input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} placeholder="https://youtu.be/…" data-testid="workout-url" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })} className={inputCls} data-testid="workout-category">
                {Object.keys(meta?.categories || {}).map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            {needSub && (
              <div><label className={labelCls}>Sub-category *</label>
                <select required value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className={inputCls} data-testid="workout-subcategory">
                  <option value="">—</option>
                  {subs.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></div>
            )}
          </div>
          <label className="flex items-center gap-3 text-sm text-white/70">
            <input type="checkbox" checked={form.dedicated} onChange={(e) => setForm({ ...form, dedicated: e.target.checked })} className="accent-[#D4AF37]" data-testid="workout-dedicated" />
            Share only with selected players ("Specially for…")
          </label>
          {form.dedicated && (
            <div>
              <input placeholder="Search players" value={pSearch} onChange={(e) => setPSearch(e.target.value)} className={inputCls + " mb-2"} data-testid="workout-player-search" />
              <div className="border border-white/10 max-h-48 overflow-y-auto" data-testid="workout-player-list">
                {filteredPlayers.map((p) => (
                  <label key={p.player_id} className="flex items-center gap-3 px-3 py-2 border-b border-white/5 hover:bg-[#D4AF37]/5 cursor-pointer" data-testid={`workout-player-${p.player_id}`}>
                    <input type="checkbox" checked={form.player_ids.includes(p.player_id)} onChange={() => togglePlayer(p.player_id)} className="accent-[#D4AF37]" />
                    <span className="text-sm">{p.name} <span className="text-[#D4AF37] text-xs">{p.player_id}</span></span>
                  </label>
                ))}
              </div>
              <p className="text-white/40 text-xs mt-2">{form.player_ids.length} player{form.player_ids.length === 1 ? "" : "s"} selected</p>
            </div>
          )}
          <div><label className={labelCls}>Notes (optional)</label><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="workout-notes" /></div>
        </div>
        <div className="flex gap-3 pt-6">
          <button type="submit" disabled={saving || (form.dedicated && form.player_ids.length === 0)} className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] disabled:opacity-50" data-testid="workout-save">
            {saving ? "Saving..." : "Add Video"}
          </button>
          <button type="button" onClick={onClose} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]">Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default function Workouts() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [dedicated, setDedicated] = useState([]);
  const [players, setPlayers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [openFolder, setOpenFolder] = useState(null);
  const [tab, setTab] = useState("Plyometrics");
  const [subTab, setSubTab] = useState("");

  const load = () => {
    api.get("/admin/workouts").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/workouts/dedicated").then((r) => setDedicated(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/admin/workouts/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/admin/players").then((r) => setPlayers(r.data)).catch(() => {});
    load();
  }, []);

  const doDelete = async () => {
    try { await api.delete(`/admin/workouts/${deleting.id}`); toast.success("Video removed"); setDeleting(null); load(); }
    catch (err) { toast.error(formatApiError(err)); throw err; }
  };

  const categories = Object.keys(meta?.categories || {});
  const subs = meta?.categories?.[tab] || [];
  const library = items.filter((v) => !v.player_ids || v.player_ids.length === 0);
  const visible = library.filter((v) => v.category === tab && (subs.length === 0 || !subTab || v.subcategory === subTab));

  return (
    <div data-testid="admin-workouts">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Player Development <span className="text-[#D4AF37]">({items.length})</span></h1>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059]" data-testid="workout-add">
          <Plus size={15} /> Add Video
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-4" data-testid="workout-tabs">
        {categories.map((c) => (
          <button key={c} onClick={() => { setTab(c); setSubTab(""); }}
            className={`text-xs uppercase tracking-widest px-4 py-2 border transition-colors duration-200 ${tab === c ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`}
            data-testid={`workout-tab-${c.toLowerCase()}`}>{c}</button>
        ))}
      </div>
      {subs.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-6" data-testid="workout-subtabs">
          {["", ...subs].map((s) => (
            <button key={s || "all"} onClick={() => setSubTab(s)}
              className={`text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors duration-150 ${subTab === s ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-white/50 hover:border-[#D4AF37]/60"}`}
              data-testid={`workout-subtab-${(s || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s || "All"}</button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <p className="text-white/40 text-sm border border-white/10 p-8 text-center" data-testid="workout-empty">No videos in {tab}{subTab ? ` · ${subTab}` : ""} yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((v) => <VideoCard key={v.id} v={v} onDelete={(vv) => setDeleting(vv)} />)}
        </div>
      )}

      <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mt-12 mb-4 flex items-center gap-2"><Folder size={14} /> Dedicated Videos — {dedicated.length} player folder{dedicated.length === 1 ? "" : "s"}</h2>
      {dedicated.length === 0 ? (
        <p className="text-white/40 text-sm border border-white/10 p-6 text-center" data-testid="dedicated-empty">No player-specific videos yet. Toggle "Share only with selected players" when adding a video.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {dedicated.map((f) => (
            <button key={f.player_id} onClick={() => setOpenFolder(f)}
              className="flex items-center gap-3 border border-white/10 bg-[#0A0A0A] p-4 hover:border-[#D4AF37] transition-colors duration-150 text-left"
              data-testid={`folder-${f.player_id}`}>
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]"><Video size={18} /></div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate">{f.name}</p>
                <p className="text-[#D4AF37] text-xs">{f.count} video{f.count === 1 ? "" : "s"}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {showAdd && <AddModal meta={meta} players={players} onClose={() => setShowAdd(false)} onSaved={load} />}

      {openFolder && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto py-10 px-4" onClick={() => setOpenFolder(null)} data-testid="folder-modal">
          <div className="max-w-4xl mx-auto bg-[#0A0A0A] border border-white/10 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-5">
              <div>
                <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Specially For</p>
                <h2 className="font-heading text-xl font-black mt-1">{openFolder.name} <span className="text-[#D4AF37] text-sm">({openFolder.count})</span></h2>
              </div>
              <button onClick={() => setOpenFolder(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {openFolder.videos.map((v) => <VideoCard key={v.id} v={v} onDelete={(vv) => { setDeleting(vv); setOpenFolder(null); }} />)}
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={doDelete}
        title="Delete Video"
        itemLabel={deleting?.title}
        firstMessage="This video will be removed from the library and every player folder it was shared to."
        secondMessage="Final confirmation. This cannot be undone."
        testid="confirm-workout-delete"
      />
    </div>
  );
}
